import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Ban, ExternalLink, Lock, LogIn, Mail, MailX, RefreshCw, ShieldAlert, ShieldCheck, UserCheck } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { signInWithGoogle, supabase } from '../../lib/supabase';
import { anmeldeHinweisLesen, rueckwegMerken, rueckwegVergessen } from '../../lib/anmeldeRueckweg';
import { ANMELDE_DOMAIN, type LinkAnmeldeStatus, type LinkAnmeldung } from '../../lib/planung';
import './Anmeldung.css';

/** Hier landen Fragen zu Zugängen (wie auf „Kein Zugang“). */
const KONTAKT = 'backoffice@randori-pro.de';

function GoogleZeichen() {
  return (
    <svg className="fba-google" viewBox="0 0 48 48" aria-hidden="true" focusable="false">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
  );
}

/** Wie mail_adresse_maske in der Datenbank: erstes Zeichen, „•••@“, Domain. */
function maske(adresse: string): string {
  const a = adresse.trim().toLowerCase();
  const at = a.indexOf('@');
  return at > 0 ? `${a[0]}•••@${a.slice(at + 1).split('@')[0]}` : `${a[0] ?? ''}•••`;
}

/**
 * Ruhige Zeile über der Mail: Mit welchem Konto ist man angemeldet? Ohne
 * passende Anmeldung zeigt der Link gar keine Mails, sondern die
 * Anmeldeseite (AnmeldeSeite) — dann erscheint diese Zeile nicht.
 */
export function AnmeldeBanner({ anmeldung }: { anmeldung: LinkAnmeldung }) {
  if (anmeldung.status !== 'ok') return null;
  return (
    <p className="fba-zeile" role="status">
      <UserCheck size={14} aria-hidden="true" />
      <span>Angemeldet als <strong>{anmeldung.angemeldet_als ?? 'Dein Konto'}</strong></span>
    </p>
  );
}

/**
 * Admin-Ansicht (Migration 0052): Ein Admin liest den Link einer anderen
 * Person mit. Deutlicher Hinweis oben — die Seite zeigt alles wie für die
 * Person, aber nur zum Lesen; kommentiert wird in der Werkstatt. Mit mailId
 * führt der Knopf zur Mail in der Werkstatt, sonst zur Übersicht.
 */
export function AdminAnsichtBanner({ anmeldung, name, mailId }: { anmeldung: LinkAnmeldung; name: string; mailId?: string | null }) {
  if (anmeldung.status !== 'admin_ansicht') return null;
  const wer = name.trim() || 'die Person';
  return (
    <section className="fba-admin" role="status" aria-label="Admin-Ansicht">
      <span className="fba-admin-symbol" aria-hidden="true"><ShieldCheck size={18} /></span>
      <div className="fba-admin-text">
        <p>
          <strong>Admin-Ansicht:</strong> So sieht {wer} die Mails. Kommentieren geht hier nur für {wer} selbst — Du kommentierst in der Werkstatt.
        </p>
        <p className="fba-admin-klein">
          Angemeldet als <strong>{anmeldung.angemeldet_als ?? 'Admin'}</strong>
          {anmeldung.adresse && <> · Link von <strong>{anmeldung.adresse}</strong></>}
          {' '}· Dein Besuch zählt nicht als „gesehen“.
        </p>
      </div>
      {mailId ? (
        <Link to={`/geplante-mails/${mailId}`} className="btn btn-primary btn-sm fba-admin-knopf">
          <ExternalLink size={14} aria-hidden="true" /> In der Werkstatt öffnen
        </Link>
      ) : (
        <Link to="/geplante-mails" className="btn btn-secondary btn-sm fba-admin-knopf">
          <ExternalLink size={14} aria-hidden="true" /> Zu Geplante Mails
        </Link>
      )}
    </section>
  );
}

/** Stände, für die es eine eigene Anmeldeseite gibt (ok und Admin-Ansicht zeigen die Mails). */
type SeitenStatus = Exclude<LinkAnmeldeStatus, 'ok' | 'admin_ansicht'>;

const SYMBOL: Record<SeitenStatus, LucideIcon> = {
  nicht_angemeldet: LogIn,
  andere_adresse: ShieldAlert,
  keine_adresse: MailX,
  nicht_erlaubt: Ban,
};

type SeiteProps = {
  anmeldung: LinkAnmeldung;
  /** Name für die Begrüßung (Vorname, bei Studioaccounts der ganze Name) */
  name?: string | null;
  /** Fuß mit Hinweis „persönlich“ — weglassen, wenn die Seite drumherum schon einen hat */
  fuss?: boolean;
};

/**
 * Ganze Seite statt der Mails, solange die Anmeldung nicht passt: Begrüßung,
 * welches Google-Konto gebraucht wird (maskierte Adresse) und der Knopf zur
 * Anmeldung. Die Datenbank liefert ohne passende Anmeldung keine Mails —
 * hier gibt es also nichts zu verstecken, nur den Weg hinein.
 */
export function AnmeldeSeite({ anmeldung, name, fuss = true }: SeiteProps) {
  const ort = useLocation();
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const { adresse, angemeldet_als: als } = anmeldung;
  const status: SeitenStatus = anmeldung.status === 'ok' || anmeldung.status === 'admin_ansicht' ? 'nicht_angemeldet' : anmeldung.status;
  const kannAnmelden = status === 'nicht_angemeldet' || status === 'andere_adresse';
  // Rückweg aus /auth/callback, wenn Google oder Supabase die Anmeldung abgelehnt hat
  const hinweis = kannAnmelden ? anmeldeHinweisLesen(ort.search) : null;
  // Richtige Adresse, aber nicht über Google angemeldet (z. B. mit Passwort in einer anderen App)
  const gleicheAdresse = status === 'andere_adresse' && !!als && !!adresse && maske(als) === adresse.toLowerCase();
  const Symbol = SYMBOL[status];

  async function anmelden(anderesKonto: boolean) {
    setLaeuft(true);
    setFehler(null);
    rueckwegMerken(ort.pathname + ort.search);
    try {
      // Anderes Konto: erst hier abmelden (nur dieses Gerät), dann fragt Google nach dem Konto.
      if (anderesKonto && supabase) await supabase.auth.signOut({ scope: 'local' });
      const { error } = await signInWithGoogle({ selectAccount: anderesKonto });
      if (error) throw error;
      // Erfolg: Der Browser geht gleich zu Google — Knopf bleibt solange gesperrt.
    } catch {
      rueckwegVergessen();
      setFehler('Die Anmeldung ließ sich gerade nicht starten. Versuch es bitte gleich noch einmal.');
      setLaeuft(false);
    }
  }

  const konto = adresse ? <strong className="fba-adresse">{adresse}</strong> : <>Deinem Google-Konto @{ANMELDE_DOMAIN}</>;
  const wozu = 'um die Mails zu sehen und zu kommentieren.';

  return (
    <div className="fba-seite">
      <header className="fba-seite-kopf">
        <span className="fba-seite-marke"><Mail size={14} aria-hidden="true" /> Randori Mail · Abstimmung</span>
      </header>
      <main className="fba-seite-haupt">
        <section className={`fba-karte fba-karte--${status}`} aria-labelledby="fba-karte-titel">
          <span className="fba-karte-symbol" aria-hidden="true"><Symbol size={26} /></span>
          <h1 id="fba-karte-titel">{name ? `Hallo ${name}` : 'Hallo'}</h1>

          {status === 'nicht_angemeldet' && (
            <p>Melde Dich mit Deinem Google-Konto {adresse ? konto : <>@{ANMELDE_DOMAIN}</>} an, {wozu}</p>
          )}
          {status === 'andere_adresse' && gleicheAdresse && (
            <p>
              Du bist als <strong className="fba-adresse">{als}</strong> angemeldet, aber nicht über Google.
              {' '}Melde Dich mit dem Google-Konto dieser Adresse an, {wozu}
            </p>
          )}
          {status === 'andere_adresse' && !gleicheAdresse && (
            <p>
              Du bist als <strong className="fba-adresse">{als ?? 'ein anderes Konto'}</strong> angemeldet — dieser Link gehört einem
              anderen Konto. Melde Dich mit Deinem Google-Konto {konto} an, {wozu}
            </p>
          )}
          {status === 'nicht_erlaubt' && (
            <p>
              Diese Links gibt es nur für Konten @{ANMELDE_DOMAIN}. Für die hinterlegte Adresse geht das nicht —
              bitte melde Dich bei der Redaktion (<a href={`mailto:${KONTAKT}`}>{KONTAKT}</a>).
            </p>
          )}
          {status === 'keine_adresse' && (
            <p>
              Für diesen Link ist noch keine Adresse @{ANMELDE_DOMAIN} hinterlegt, deshalb klappt die Anmeldung nicht.
              Bitte melde Dich bei der Redaktion (<a href={`mailto:${KONTAKT}`}>{KONTAKT}</a>).
            </p>
          )}

          {hinweis === 'fehlgeschlagen' && (
            <p className="fba-fehler" role="alert">
              Die Anmeldung hat nicht geklappt. Prüf, ob Du das richtige Google-Konto gewählt hast — sonst melde Dich bitte bei der Redaktion.
            </p>
          )}
          {hinweis === 'abgebrochen' && (
            <p className="fba-zusatz" role="status">Die Anmeldung wurde abgebrochen. Du kannst es jederzeit noch einmal versuchen.</p>
          )}
          {fehler && <p className="fba-fehler" role="alert">{fehler}</p>}

          {kannAnmelden && (
            <button
              type="button"
              className="fba-knopf fba-knopf--gross"
              onClick={() => { void anmelden(status === 'andere_adresse'); }}
              disabled={laeuft}
              aria-busy={laeuft || undefined}
            >
              {laeuft ? <RefreshCw size={16} aria-hidden="true" className="spin" /> : <GoogleZeichen />}
              <span>{status === 'andere_adresse' && !gleicheAdresse ? 'Mit anderem Konto anmelden' : 'Mit Google anmelden'}</span>
            </button>
          )}
        </section>
      </main>
      {fuss && (
        <footer className="fba-seite-fuss">
          <p><Lock size={12} aria-hidden="true" /> Dieser Link ist persönlich — bitte nicht weitergeben.</p>
          <p>© {new Date().getFullYear()} Randori-Pro Management GmbH</p>
        </footer>
      )}
    </div>
  );
}
